import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import * as React from "react";
import { cx } from "../internal/cx.js";
import { useIsoLayoutEffect } from "./scroller.js";
const FilterBarContext = React.createContext(null);
function firstButton(container) {
  return container?.querySelector("button:not([disabled])") ?? null;
}
function focusLost() {
  const active = typeof document === "undefined" ? null : document.activeElement;
  return !active || active === document.body;
}
function forgetIfStillShown(pending) {
  return (event) => {
    if (event.currentTarget.isConnected) pending.current = false;
  };
}
const FilterBar = React.forwardRef(function FilterBar2(props, forwardedRef) {
  const { onClearAll, clearAllLabel = "Clear filters", activeCount, className, children, ...rest } = props;
  const barRef = React.useRef(null);
  React.useImperativeHandle(forwardedRef, () => barRef.current, []);
  const [registered, setRegistered] = React.useState(0);
  const register = React.useCallback(() => {
    setRegistered((count2) => count2 + 1);
    return () => setRegistered((count2) => count2 - 1);
  }, []);
  const count = activeCount ?? registered;
  const refocusRef = React.useRef(false);
  useIsoLayoutEffect(() => {
    if (!refocusRef.current) return;
    refocusRef.current = false;
    if (count === 0 && focusLost()) firstButton(barRef.current)?.focus({ preventScroll: true });
  }, [count]);
  const label = rest["aria-label"] ?? (rest["aria-labelledby"] ? void 0 : "Filters");
  return /* @__PURE__ */ jsx(FilterBarContext.Provider, { value: register, children: /* @__PURE__ */ jsxs("div", { ...rest, ref: barRef, role: "group", "aria-label": label, className: cx("ev-filterbar", className), children: [
    children,
    onClearAll && count > 0 ? /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: "ev-btn ev-btn--ghost ev-filterbar__clear",
        onClick: () => {
          refocusRef.current = true;
          onClearAll();
        },
        onBlur: forgetIfStillShown(refocusRef),
        children: clearAllLabel
      }
    ) : null
  ] }) });
});
function hasValue(value) {
  return value !== void 0 && value !== null && value !== false && value !== "";
}
const FilterChip = React.forwardRef(function FilterChip2(props, forwardedRef) {
  const { label, value, active: activeProp, onClear, clearLabel, variant = "add", className, ...rest } = props;
  const active = activeProp ?? hasValue(value);
  const mainRef = React.useRef(null);
  React.useImperativeHandle(forwardedRef, () => mainRef.current, []);
  const register = React.useContext(FilterBarContext);
  useIsoLayoutEffect(() => {
    if (!active || !register) return void 0;
    return register();
  }, [active, register]);
  const refocusRef = React.useRef(false);
  useIsoLayoutEffect(() => {
    if (!refocusRef.current) return;
    refocusRef.current = false;
    if (!active && focusLost()) mainRef.current?.focus({ preventScroll: true });
  }, [active]);
  const clearName = clearLabel ?? (typeof label === "string" || typeof label === "number" ? `Clear ${label} filter` : "Clear filter");
  const showClear = active && Boolean(onClear);
  const addChip = !active && variant !== "plain";
  return /* @__PURE__ */ jsxs("span", { className: cx("ev-filter-chip", className), "data-active": active ? "true" : "false", children: [
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        ...rest,
        ref: mainRef,
        className: cx("ev-chip", addChip && "ev-chip--add", showClear && "ev-chip--clearable"),
        "data-active": active ? "true" : "false",
        children: active ? /* @__PURE__ */ jsxs(Fragment, { children: [
          label,
          ": ",
          /* @__PURE__ */ jsx("b", { children: value })
        ] }) : addChip ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("span", { className: "ev-chip__plus", "aria-hidden": "true", children: "+" }),
          label
        ] }) : label
      }
    ),
    showClear ? /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: "ev-chip__clear",
        "aria-label": clearName,
        disabled: rest.disabled,
        onClick: () => {
          refocusRef.current = true;
          onClear?.();
        },
        onBlur: forgetIfStillShown(refocusRef),
        children: /* @__PURE__ */ jsx("svg", { viewBox: "0 0 10 10", "aria-hidden": "true", focusable: "false", children: /* @__PURE__ */ jsx("path", { d: "M2 2l6 6M8 2l-6 6", fill: "none", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round" }) })
      }
    ) : null
  ] });
});
export {
  FilterBar,
  FilterChip
};
//# sourceMappingURL=FilterBar.js.map
